import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewEncapsulation } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ObjDetailsData } from "../s25.object.details.component";
import { ContactService } from "../../../services/contact.service";
import { Bind } from "../../../decorators/bind.decorator";
import { Item } from "../../../pojo/Item";

@TypeManagerDecorator("s25-ng-object-details-general")
@Component({
    selector: "s25-ng-object-details-general",
    template: `
        @switch (itemTypeId) {
            @case (Item.Ids.Contact) {
                <ng-container *ngTemplateOutlet="contact"></ng-container>
            }
        }

        <!-- Object-specific templates -->
        <ng-template #contact>
            <div class="general-details-wrapper contact-details">
                <div>
                    <label for="contact-name">Contact Name </label>
                    <p id="contact-name">{{ model.contactName }}</p>
                </div>
                @if (canEdit) {
                    <div>
                        <label>First Name</label>
                        <s25-ng-editable-text
                            [val]="model.firstName"
                            [max]="40"
                            [readOnly]="!canEdit"
                            [hasCommit]="true"
                            [onCommit]="save"
                            [ariaLabel]="'First Name'"
                            (valChange)="onUpdate($event, 'first_name')"
                        ></s25-ng-editable-text>
                    </div>
                    <div>
                        <label> Middle Name </label>
                        <s25-ng-editable-text
                            [val]="model.middleName"
                            [max]="80"
                            [readOnly]="!canEdit"
                            [hasCommit]="true"
                            [onCommit]="save"
                            [ariaLabel]="'Middle Name'"
                            (valChange)="onUpdate($event, 'middle_name')"
                        ></s25-ng-editable-text>
                    </div>
                    <div>
                        <label> Last Name </label>
                        <s25-ng-editable-text
                            [val]="model.lastName"
                            [max]="40"
                            [readOnly]="!canEdit"
                            [hasCommit]="true"
                            [onCommit]="save"
                            [ariaLabel]="'Last Name'"
                            (valChange)="onUpdate($event, 'last_name')"
                        ></s25-ng-editable-text>
                    </div>
                }
                @if (model.namePrefix || canEdit) {
                    <div>
                        <label> Prefix </label>
                        <s25-ng-editable-text
                            [val]="model.namePrefix"
                            [max]="20"
                            [readOnly]="!canEdit"
                            [hasCommit]="true"
                            [onCommit]="save"
                            [ariaLabel]="'Prefix'"
                            (valChange)="onUpdate($event, 'name_prefix')"
                        ></s25-ng-editable-text>
                    </div>
                }
                @if (model.nameSuffix || canEdit) {
                    <div>
                        <label> Suffix </label>
                        <s25-ng-editable-text
                            [val]="model.nameSuffix"
                            [max]="20"
                            [readOnly]="!canEdit"
                            [hasCommit]="true"
                            [onCommit]="save"
                            [ariaLabel]="'Suffix'"
                            (valChange)="onUpdate($event, 'name_suffix')"
                        ></s25-ng-editable-text>
                    </div>
                }
                @if (model.nameSuffix || canEdit) {
                    <div>
                        <label> Title </label>
                        <s25-ng-editable-text
                            [val]="model.title"
                            [max]="40"
                            [readOnly]="!canEdit"
                            [hasCommit]="true"
                            [onCommit]="save"
                            [ariaLabel]="'Title'"
                            (valChange)="onUpdate($event, 'title')"
                        ></s25-ng-editable-text>
                    </div>
                }
                @if (model.internalId || canEdit) {
                    <div>
                        <label> Internal ID </label>
                        <s25-ng-editable-text
                            [val]="model.internalId"
                            [max]="30"
                            [readOnly]="!canEdit"
                            [hasCommit]="true"
                            [onCommit]="save"
                            [ariaLabel]="'Internal ID'"
                            (valChange)="onUpdate($event, 'internal_id')"
                        ></s25-ng-editable-text>
                    </div>
                }
            </div>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25ObjectDetailsGeneralComponent {
    @Input({ required: true }) model: ObjDetailsData["general"];
    @Input({ required: true }) itemTypeId: Item.Id;
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) canEdit: boolean;

    dataToUpdate: { prop: string; value: string };

    constructor(private cd: ChangeDetectorRef) {}

    @Bind
    save() {
        try {
            switch (this.itemTypeId) {
                case Item.Ids.Contact:
                    return this.saveContact();
            }
        } catch (error) {
            console.error(error);
        }
    }

    @Bind
    onUpdate(value: string, prop: string) {
        this.dataToUpdate = { prop, value };
    }

    async saveContact() {
        const { prop, value } = this.dataToUpdate;
        const resp = await ContactService.putContactMinimal(this.itemId, { [prop]: value });
        const props = ["first_name", "middle_name", "last_name"];
        if (resp?.results?.info?.msg_id === "CO_I_SAVE" && props.includes(prop)) {
            const name = await ContactService.getContactName(this.itemId);
            name && (this.model.contactName = name);
            this.cd.markForCheck();
        }
    }

    protected readonly Item = Item;
}
